import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import DiosaForm from './DiosaForm';
import { track } from '../lib/tracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = 'diosa_popup_seen';
const VISITED_KEY = 'diosa_visited';

const readSeen = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}'); } catch { return {}; }
};

/* Frequency capping lives in browser storage so a refresh never re-opens a popup. */
const allowed = (popup) => {
  const seen = readSeen();
  const last = seen[popup.id];
  const freq = popup.frequency || 'once_per_session';
  if (freq === 'always') return true;
  if (freq === 'once_per_session') return !sessionStorage.getItem(`${SEEN_KEY}:${popup.id}`);
  if (!last) return true;
  if (freq === 'first_visit') return false;
  const days = freq === 'once_per_day' ? 1 : Math.max(1, popup.frequency_days || 7);
  return (Date.now() - last) > days * 86400000;
};

const markSeen = (popup) => {
  const seen = readSeen();
  seen[popup.id] = Date.now();
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch { /* private mode */ }
  sessionStorage.setItem(`${SEEN_KEY}:${popup.id}`, '1');
};

const HIDDEN_PREFIXES = ['/admin', '/pos', '/barcode-sheet', '/checkout'];

export const PopupHost = () => {
  const { pathname, search } = useLocation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const armed = useRef(false);

  const hidden = HIDDEN_PREFIXES.some(p => pathname.startsWith(p));

  const load = useCallback(async () => {
    setData(null); setOpen(false); armed.current = false;
    if (hidden) return;
    const visitor = localStorage.getItem(VISITED_KEY) ? 'returning' : 'new';
    try { localStorage.setItem(VISITED_KEY, '1'); } catch { /* private mode */ }
    const params = new URLSearchParams({ path: pathname, visitor });
    const m = pathname.match(/^\/products\/([^/]+)/);
    if (m) params.set('product_id', m[1]);
    const room = pathname.match(/^\/rooms\/([^/]+)/);
    if (room) params.set('room', room[1]);
    const cat = new URLSearchParams(search).get('category');
    if (cat) params.set('category', cat);
    try {
      const { data: res } = await axios.get(`${API_URL}/api/popups/active?${params.toString()}`, { withCredentials: true });
      if (res.popup && allowed(res.popup)) setData(res);
    } catch { /* popups are never allowed to break a page */ }
  }, [pathname, search, hidden]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data?.popup || armed.current) return;
    const p = data.popup;
    const show = () => {
      if (armed.current) return;
      armed.current = true;
      setOpen(true);
      markSeen(p);
      axios.post(`${API_URL}/api/popups/${p.id}/impression`).catch(() => { /* stats only */ });
      if (data.form) axios.post(`${API_URL}/api/forms/${data.form.slug}/view`).catch(() => { /* stats only */ });
    };

    let timer;
    const onScroll = () => {
      const pct = (window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight)) * 100;
      if (pct >= (p.scroll_percent || 40)) show();
    };
    const onLeave = (e) => { if (e.clientY <= 0) show(); };

    if (p.trigger === 'immediate') show();
    else if (p.trigger === 'scroll') window.addEventListener('scroll', onScroll, { passive: true });
    else if (p.trigger === 'exit_intent') document.addEventListener('mouseout', onLeave);
    else timer = setTimeout(show, (p.delay_seconds || 6) * 1000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onLeave);
    };
  }, [data]);

  if (!open || !data?.popup) return null;
  const p = data.popup;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => { track('popup_closed', { popup_id: p.id }); setOpen(false); }} data-testid="site-popup">
      <div className="bg-[#FAF6EF] w-full max-w-3xl max-h-[92vh] overflow-y-auto relative shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <button onClick={() => { track('popup_closed', { popup_id: p.id }); setOpen(false); }}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white text-[#1B1815]"
          aria-label="Close" data-testid="site-popup-close">
          <X size={16} />
        </button>

        <div className={`grid ${p.image_url ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
          {p.image_url && (
            <div className="hidden md:block" style={{ aspectRatio: '3 / 4' }}>
              <img src={p.image_url} alt="" className="w-full h-full object-cover" data-testid="site-popup-image" />
            </div>
          )}
          <div className="p-7 sm:p-9">
            {p.heading && (
              <h2 className="text-2xl sm:text-3xl leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="site-popup-heading">
                {p.heading}
              </h2>
            )}
            {p.supporting_text && (
              <p className="text-[12px] text-[#595956] leading-relaxed mb-4" data-testid="site-popup-text">{p.supporting_text}</p>
            )}
            {p.offer_text && (
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#B08D57] mb-5" data-testid="site-popup-offer">{p.offer_text}</p>
            )}

            {data.form ? (
              <DiosaForm form={data.form} popupId={p.id} compact />
            ) : (
              <>
                {data.coupon && (
                  <div className="border border-[#B08D57] bg-white p-4 mb-4 text-center" data-testid="site-popup-coupon">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#595956] mb-1">{data.coupon.label}</p>
                    <p className="font-mono text-lg tracking-wider" data-testid="site-popup-coupon-code">{data.coupon.code}</p>
                    <p className="text-[10px] text-[#595956] mt-2">Apply it yourself at checkout.</p>
                  </div>
                )}
                {p.cta_text && (
                  <a href={p.cta_link || '/products'} onClick={() => track('popup_clicked', { popup_id: p.id, label: p.cta_text })}
                    className="btn-luxe text-[10px] py-2.5 px-6 inline-block" data-testid="site-popup-cta">
                    {p.cta_text}
                  </a>
                )}
              </>
            )}

            {p.download_url && !data.form && (
              <a href={p.download_url} target="_blank" rel="noreferrer"
                className="block text-[10px] uppercase tracking-[0.2em] text-[#B08D57] mt-4" data-testid="site-popup-download">
                {p.download_label || 'Download'}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupHost;
